// Shared UI components. Each returns { node, ... } with a small imperative API.
import { el, nextId, replace, copyText, announce } from './dom.js';
import { fmt, parseDecimal } from './format.js';

/**
 * Number input with label, unit, inline validation.
 * onChange(value) fires with a finite number only when the input is valid.
 */
export function NumberField({ label, unit, value, min, max, step = 'any', hint, name, decimals = 2, onChange, validate }) {
  const id = nextId('nf');
  const errId = id + '-err';
  const hintId = hint ? id + '-hint' : null;
  const input = el('input', {
    id,
    name: name || id,
    type: 'text',
    inputmode: 'decimal',
    autocomplete: 'off',
    class: 'tool-field__input',
    'aria-describedby': [hintId].filter(Boolean).join(' ') || null,
    dataset: { min: min ?? '', max: max ?? '' },
  });
  const err = el('p', { id: errId, class: 'tool-field__error', hidden: true });
  const node = el(
    'div',
    { class: 'tool-field' },
    el('label', { for: id, class: 'tool-field__label' }, label),
    el('div', { class: 'tool-field__control' }, input, unit ? el('span', { class: 'tool-field__unit', 'aria-hidden': 'true' }, unit) : null),
    hint ? el('p', { id: hintId, class: 'tool-field__hint' }, hint) : null,
    err,
  );

  function show(v) {
    input.value = Number.isFinite(v) ? fmt(v, { min: 0, max: decimals }).replace(/\./g, '') : '';
  }
  function check() {
    const v = parseDecimal(input.value);
    let msg = '';
    if (!Number.isFinite(v)) msg = 'Bitte eine Zahl eingeben.';
    else if (min !== undefined && v < min) msg = `Mindestens ${fmt(min, { max: 3 })}${unit ? ' ' + unit : ''}.`;
    else if (max !== undefined && v > max) msg = `Höchstens ${fmt(max, { max: 3 })}${unit ? ' ' + unit : ''}.`;
    else if (validate) msg = validate(v) || '';
    setError(msg);
    return msg ? NaN : v;
  }
  function setError(msg) {
    err.textContent = msg;
    err.hidden = !msg;
    input.setAttribute('aria-invalid', msg ? 'true' : 'false');
    const ids = [hintId, msg ? errId : null].filter(Boolean).join(' ');
    if (ids) input.setAttribute('aria-describedby', ids);
    else input.removeAttribute('aria-describedby');
    node.classList.toggle('is-invalid', !!msg);
  }
  input.addEventListener('input', () => {
    const v = check();
    if (Number.isFinite(v) && onChange) onChange(v);
  });
  input.addEventListener('blur', () => {
    const v = check();
    if (Number.isFinite(v)) show(v);
  });
  show(value);
  return {
    node,
    input,
    get value() {
      return parseDecimal(input.value);
    },
    set value(v) {
      show(v);
      setError('');
    },
    get valid() {
      return !node.classList.contains('is-invalid');
    },
    setError,
  };
}

/** Counter with − / + buttons, arrow keys, range clamp. */
export function Stepper({ label, value = 0, min = 0, max = 50, step = 1, onChange, labelNode, describedBy }) {
  const id = nextId('st');
  let current = value;
  const input = el('input', {
    id,
    type: 'text',
    inputmode: 'numeric',
    class: 'tool-stepper__input',
    autocomplete: 'off',
    'aria-describedby': describedBy || null,
  });
  const dec = el('button', { type: 'button', class: 'tool-stepper__btn', 'aria-label': `${label}: weniger` }, '−');
  const inc = el('button', { type: 'button', class: 'tool-stepper__btn', 'aria-label': `${label}: mehr` }, '+');
  const node = el(
    'div',
    { class: 'tool-stepper' },
    el('label', { for: id, class: 'tool-stepper__label' }, labelNode || label),
    el('div', { class: 'tool-stepper__control' }, dec, input, inc),
  );
  function set(v, notify = true) {
    const n = Math.round(Number.isFinite(v) ? v : min);
    current = Math.min(max, Math.max(min, n));
    input.value = String(current);
    dec.disabled = current <= min;
    inc.disabled = current >= max;
    if (notify && onChange) onChange(current);
  }
  dec.addEventListener('click', () => set(current - step));
  inc.addEventListener('click', () => set(current + step));
  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      set(current + step);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      set(current - step);
    }
  });
  input.addEventListener('input', () => {
    const raw = input.value.trim();
    if (raw === '') return;
    const n = parseDecimal(raw);
    if (Number.isFinite(n)) {
      current = Math.min(max, Math.max(min, Math.round(n)));
      dec.disabled = current <= min;
      inc.disabled = current >= max;
      if (onChange) onChange(current);
    }
  });
  input.addEventListener('blur', () => set(current, false));
  set(value, false);
  return {
    node,
    input,
    get value() {
      return current;
    },
    set value(v) {
      set(v, false);
    },
  };
}

/** Native select. options: [{value, label}] */
export function Select({ label, options, value, onChange, hint }) {
  const id = nextId('sel');
  const select = el(
    'select',
    { id, class: 'tool-select__input' },
    options.map((o) => el('option', { value: o.value, selected: o.value === value }, o.label)),
  );
  select.value = value;
  select.addEventListener('change', () => onChange && onChange(select.value));
  const node = el(
    'div',
    { class: 'tool-field tool-select' },
    el('label', { for: id, class: 'tool-field__label' }, label),
    el('div', { class: 'tool-select__wrap' }, select),
    hint ? el('p', { class: 'tool-field__hint' }, hint) : null,
  );
  return {
    node,
    select,
    get value() {
      return select.value;
    },
    set value(v) {
      select.value = v;
    },
  };
}

/** Segmented control (radio group shown as pills), for 2–4 options. */
export function Segmented({ label, options, value, onChange, name }) {
  const groupName = name || nextId('seg');
  const inputs = [];
  const node = el(
    'fieldset',
    { class: 'tool-segmented' },
    el('legend', { class: 'tool-field__label' }, label),
    el(
      'div',
      { class: 'tool-segmented__options' },
      options.map((o) => {
        const id = nextId('segopt');
        const input = el('input', { type: 'radio', name: groupName, id, value: o.value, checked: o.value === value, class: 'tool-segmented__radio' });
        input.addEventListener('change', () => input.checked && onChange && onChange(o.value));
        inputs.push(input);
        return el('label', { for: id, class: 'tool-segmented__option' }, input, el('span', {}, o.label));
      }),
    ),
  );
  return {
    node,
    get value() {
      const c = inputs.find((i) => i.checked);
      return c ? c.value : undefined;
    },
    set value(v) {
      inputs.forEach((i) => {
        i.checked = i.value === v;
      });
    },
  };
}

/** Checkbox with label. */
export function Checkbox({ label, checked = false, onChange, hint }) {
  const id = nextId('cb');
  const input = el('input', { type: 'checkbox', id, checked, class: 'tool-check__input' });
  input.addEventListener('change', () => onChange && onChange(input.checked));
  const node = el(
    'div',
    { class: 'tool-check' },
    input,
    el('label', { for: id, class: 'tool-check__label' }, label, hint ? el('span', { class: 'tool-field__hint' }, hint) : null),
  );
  return {
    node,
    input,
    get value() {
      return input.checked;
    },
    set value(v) {
      input.checked = !!v;
    },
  };
}

/**
 * Result card(s). items: [{ label, value, unit, sub, status }]
 * Single aria-live region wraps all figures.
 */
export function ResultCards({ label = 'Ergebnis' } = {}) {
  const grid = el('div', { class: 'tool-results__grid' });
  const note = el('p', { class: 'tool-results__note' });
  const node = el('div', { class: 'tool-results', 'aria-live': 'polite', 'aria-label': label, role: 'region' }, grid, note);
  return {
    node,
    update(items, noteText = '') {
      replace(
        grid,
        items.map((it) =>
          el(
            'div',
            { class: 'tool-result' + (it.status ? ' tool-result--' + it.status : '') },
            el('p', { class: 'tool-result__label' }, it.label),
            el('p', { class: 'tool-result__value' }, el('span', { class: 'tool-result__num' }, it.value), it.unit ? el('span', { class: 'tool-result__unit' }, it.unit) : null),
            it.sub ? el('p', { class: 'tool-result__sub' }, it.sub) : null,
          ),
        ),
      );
      note.textContent = noteText;
      note.hidden = !noteText;
    },
  };
}

export const SOURCE_LABELS = {
  gemessen: 'gemessen',
  hersteller: 'Hersteller',
  quelle: 'Quelle',
  schaetzung: 'Schätzung',
  eigen: 'eigener Wert',
};

const SOURCE_TITLES = {
  gemessen: 'Selbst gemessen',
  hersteller: 'Herstellerangabe',
  quelle: 'Aus Presse oder Community, mit Quelle',
  schaetzung: 'Schätzung – noch nicht belegt',
  eigen: 'Von dir eingetragen',
};

/** Provenance chip. */
export function SourceBadge(source, { url, note, date } = {}) {
  const key = SOURCE_LABELS[source] ? source : 'schaetzung';
  const title = [SOURCE_TITLES[key], note, date ? 'Stand ' + date : ''].filter(Boolean).join(' · ');
  const attrs = { class: `tool-badge tool-badge--${key}`, title };
  if (url) return el('a', { ...attrs, href: url, rel: 'noopener', target: '_blank' }, SOURCE_LABELS[key]);
  return el('span', attrs, SOURCE_LABELS[key]);
}

/** Callout using the site's box classes. variant: '' | 'amber' */
export function Callout(variant, ...children) {
  return el('div', { class: 'box' + (variant ? ' box--' + variant : '') + ' tool-callout' }, ...children);
}

/** Status marker: text + symbol, never color alone. status: ok|warn|bad|none */
export function Status(status, text) {
  const sym = { ok: '✓', warn: '–', bad: '✗', none: '·' }[status] || '·';
  return el('span', { class: `tool-status tool-status--${status}` }, el('span', { 'aria-hidden': 'true' }, sym + ' '), text);
}

/** Copy button for the plain-text result returned by getText(). */
export function ShareBar({ getText, onShare }) {
  const msg = el('span', { class: 'tool-share__msg', role: 'status', 'aria-live': 'polite' });
  const btn = (label, fn, action) =>
    el(
      'button',
      {
        type: 'button',
        class: 'tool-copy-btn',
        onclick: async () => {
          const ok = await copyText(fn());
          msg.textContent = ok ? 'Kopiert.' : 'Kopieren nicht möglich.';
          setTimeout(() => {
            msg.textContent = '';
          }, 2500);
          if (ok && onShare) onShare(action);
        },
      },
      label,
    );
  const node = el('div', { class: 'tool-share' }, btn('Als Text kopieren', getText, 'copy-text'), msg);
  return { node };
}

/**
 * Compact table. columns: [{ key, label, align: 'right'|'left', wide, render(row) }]
 * wide: text column that keeps a minimum width on narrow screens (class is-wide) instead of
 * being squeezed by the numeric columns.
 */
const cellClass = (c) => [c.align === 'right' ? 'is-num' : '', c.wide ? 'is-wide' : ''].filter(Boolean).join(' ');

export function Table({ caption, columns, rows, footer, className = '' }) {
  const table = el(
    'table',
    { class: 'tool-table ' + className },
    caption ? el('caption', { class: 'visually-hidden' }, caption) : null,
    el('thead', {}, el('tr', {}, columns.map((c) => el('th', { scope: 'col', class: cellClass(c) }, c.label)))),
    el(
      'tbody',
      {},
      rows.map((r) =>
        el(
          'tr',
          { class: r._class || '' },
          columns.map((c, i) => {
            const content = c.render ? c.render(r) : r[c.key];
            return el(i === 0 && c.header !== false ? 'th' : 'td', { scope: i === 0 && c.header !== false ? 'row' : null, class: cellClass(c) }, content);
          }),
        ),
      ),
    ),
    footer ? el('tfoot', {}, el('tr', {}, footer.map((f, i) => el(i === 0 ? 'th' : 'td', { scope: i === 0 ? 'row' : null, class: columns[i] ? cellClass(columns[i]) : '' }, f)))) : null,
  );
  return el('div', { class: 'tool-table-wrap', tabindex: '0', role: 'region', 'aria-label': caption || 'Tabelle' }, table);
}

/** Accordion section built on <details>. */
export function Disclosure({ summary, open = false, className = '' }, ...children) {
  const sum = el('summary', { class: 'tool-acc__summary' }, summary);
  const body = el('div', { class: 'tool-acc__body' }, ...children);
  const node = el('details', { class: 'tool-acc ' + className, open }, sum, body);
  return { node, summary: sum, body };
}

export function ResetLink(onReset) {
  return el(
    'p',
    { class: 'tool-reset' },
    el(
      'button',
      {
        type: 'button',
        class: 'tool-linkbtn',
        onclick: () => {
          onReset();
          announce('Eingaben zurückgesetzt.');
        },
      },
      'Eingaben zurücksetzen',
    ),
  );
}

export function Section(title, ...children) {
  return el('section', { class: 'tool-section' }, title ? el('h3', { class: 'tool-section__title' }, title) : null, ...children);
}
